import { inject, Injectable } from '@angular/core';
import { LoggedInStaff } from './auth.interface';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { loginUrl } from '../../classes/general';
import { environment } from '../../../environments/environment';

const STAFF_STORAGE_KEY =  environment.STAFF_STORAGE_KEY //"loggedInStaff";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor() { }
  
	_http = inject(HttpClient);
	storeLoggedInStaffDetails(staff: any) {
		if (staff == null) return;
		localStorage.setItem(STAFF_STORAGE_KEY, JSON.stringify(staff));
		this.scheduleAutoLogout();
	}

	removeLoggedInStaffDetails() {
		localStorage.removeItem(STAFF_STORAGE_KEY);
		this.clearAutoLogout();
	}

	private logoutTimer: ReturnType<typeof setTimeout> | null = null;

	// Decodes a JWT's payload without verifying its signature — only used to read
	// the `exp` claim client-side; the backend is still the source of truth on
	// every real request (see errors.interceptor.ts's 401 handling).
	private decodeJwtExpiryMs(token: string): number | null {
		try {
			const payloadPart = token.split('.')[1];
			if (!payloadPart) return null;
			const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
			const json = decodeURIComponent(
				atob(base64)
					.split('')
					.map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
					.join('')
			);
			const payload = JSON.parse(json);
			return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
		} catch {
			return null;
		}
	}

	// Proactively logs the user out the moment their token expires, instead of
	// waiting for the next API call to fail with a 401. Called on app startup
	// (already-logged-in case) and again right after every fresh login.
	scheduleAutoLogout(): void {
		this.clearAutoLogout();
		const token = this.getLoggedInStaff()?.token;
		if (!token) return;

		const expiryMs = this.decodeJwtExpiryMs(token);
		if (!expiryMs) return; // not a decodable JWT, or has no exp claim

		const delay = expiryMs - Date.now();
		if (delay <= 0) {
			this.forceLogoutDueToExpiry();
			return;
		}
		this.logoutTimer = setTimeout(() => this.forceLogoutDueToExpiry(), delay);
	}

	private clearAutoLogout(): void {
		if (this.logoutTimer) {
			clearTimeout(this.logoutTimer);
			this.logoutTimer = null;
		}
	}

	private forceLogoutDueToExpiry(): void {
		this.removeLoggedInStaffDetails();
		// Full navigation, not the Angular router, so every in-memory service state
		// resets cleanly — same as a real logout should.
		window.location.href = '/login';
	}

	getLoggedInStaff(): LoggedInStaff | null {
		try {
			const storedItem = localStorage.getItem(STAFF_STORAGE_KEY);
			if (!storedItem) return null;
			return JSON.parse(storedItem) as any;
		} catch {
			localStorage.removeItem(STAFF_STORAGE_KEY);
			return null;
		}
	}


	login(data: any) {
		return this._http.post<any>(`${loginUrl}/auth/admin/login`, data);
	}

	changePassword(payload: { change_password_token: string; password: string; password_confirmation: string }) {
		return this._http.post<any>(`${loginUrl}/auth/admin/change-password`, payload);
	}

	forgotPassword(email: string) {
		return this._http.post<any>(`${loginUrl}/auth/admin/forgot-password`, { email });
	}

	resetPassword(payload: { token: string; password: string; password_confirmation: string }) {
		return this._http.post<any>(`${loginUrl}/auth/admin/reset-password`, payload, {
			headers: { 'X-Skip-Error-Toast': 'true' }
		});
	}

	// Checks whether a reset link's token still works, without consuming it —
	// safe to call more than once (e.g. on page load, before the admin has
	// typed anything).
	verifyResetToken(token: string) {
		return this._http.post<any>(`${loginUrl}/auth/admin/reset-password/verify`, { token }, {
			headers: { 'X-Skip-Error-Toast': 'true' }
		});
	}

	get2faStatus(loginToken: string) {
		return this._http.get<any>(`${loginUrl}/2fa/status`, {
			headers: { 'X-Skip-Error-Toast': 'true', Authorization: `Bearer ${loginToken}` }
		});
	}

	initiate2faSetup(loginToken?: string) {
		const headers: Record<string, string> = { 'X-Skip-Error-Toast': 'true' };
		if (loginToken) headers['Authorization'] = `Bearer ${loginToken}`;
		const body: Record<string, string> = {};
		if (loginToken) body['login_token'] = loginToken;
		return this._http.post<any>(`${loginUrl}/2fa/setup`, body, { headers });
	}

	verify2fa(otp: string, loginToken: string) {
		return this._http.post<any>(`${loginUrl}/2fa/verify`, { otp }, {
			headers: { 'X-Skip-Error-Toast': 'true', Authorization: `Bearer ${loginToken}` }
		});
	}

	// Permissions are resolved fresh on every backend request now (no longer frozen
	// into the login JWT for its full life), so a role change takes effect on the
	// backend immediately — but our cached copy from login only knows about it once
	// this is called. Returns { user_id, email, roles, permissions, permissions_by_module }
	// — permissions_by_module is no longer reliably present on the login/2fa
	// responses themselves, so this endpoint is now the source of truth for it too.
	getMyPermissions() {
		return this._http.get<any>(`${loginUrl}/admin/permissions/me`);
	}

	// Refreshes the cached roles/permissions (flat and grouped by module) from the
	// source of truth above. Returns an observable that completes once the cache
	// has actually been updated, so callers (e.g. to rebuild the menu) can react
	// at the right time instead of racing this in the background.
	refreshPermissions(): Observable<void> {
		if (!this.getLoggedInStaff()) return of(void 0);
		return this.getMyPermissions().pipe(
			map((res: any) => {
				const data = res?.data ?? res ?? {};
				const staff = this.getLoggedInStaff();
				if (staff) {
					this.storeLoggedInStaffDetails({
						...staff,
						user: {
							...staff.user,
							roles: data.roles ?? staff.user?.roles,
							permissions: data.permissions ?? staff.user?.permissions,
							permissions_by_module: data.permissions_by_module ?? staff.user?.permissions_by_module,
						}
					});
				}
			}),
			catchError(() => of(void 0))
		);
	}

	challenge2fa(otp: string, loginToken: string) {
		return this._http.post<any>(`${loginUrl}/2fa/challenge`, { login_token: loginToken, otp }, {
			headers: { 'X-Skip-Error-Toast': 'true' }
		});
	}

	storePending2faData(data: any) {
		sessionStorage.setItem('pending_2fa_data', JSON.stringify(data));
	}

	getPending2faData(): any {
		const raw = sessionStorage.getItem('pending_2fa_data');
		return raw ? JSON.parse(raw) : null;
	}

	clearPending2faData() {
		sessionStorage.removeItem('pending_2fa_data');
	}

	isSuperAdmin(): boolean {
		const staff = this.getLoggedInStaff();
		if (staff?.user?.permissions?.includes('AccessAll')) return true;
		return staff?.user?.roles?.some(r => r.toLowerCase() === 'super-admin') ?? false;
	}

	hasPermission(permission: string): boolean {
		if (this.isSuperAdmin()) return true;
		return this.getLoggedInStaff()?.user?.permissions?.includes(permission) ?? false;
	}

	hasRole(role: string): boolean {
		return this.getLoggedInStaff()?.user?.roles?.includes(role) ?? false;
	}

	getAccessibleModuleSlugs(): string[] {
		if (this.isSuperAdmin()) return ['*'];
		return (this.getLoggedInStaff()?.user?.permissions_by_module ?? []).map(m => m.slug);
	}

	hasModuleAccess(slug: string): boolean {
		if (this.isSuperAdmin()) return true;
		return this.getAccessibleModuleSlugs().includes(slug);
	}

	hasModulePermission(slug: string, permission: string): boolean {
		if (this.isSuperAdmin()) return true;
		const mod = (this.getLoggedInStaff()?.user?.permissions_by_module ?? []).find(m => m.slug === slug);
		return mod?.permissions?.includes(permission) ?? false;
	}

	hasAnyModuleAccess(slugs: string[]): boolean {
		if (this.isSuperAdmin()) return true;
		return slugs.some(slug => this.hasModuleAccess(slug));
	}

	getFirstAccessibleRoute(): string {
		if (this.isSuperAdmin()) return '/dashboard';

		const priority: { slugs: string[]; route: string }[] = [
			{ slugs: ['dashboard_analytics'],                                   route: '/dashboard' },
			{ slugs: ['order_management'],                                      route: '/sales' },
			{ slugs: ['product_catalog'],                                       route: '/catelog/product' },
			{ slugs: ['user_management'],                                       route: '/users/customers' },
			{ slugs: ['seller_management'],                                     route: '/users/merchants' },
			{ slugs: ['operations'],                                            route: '/MyMerchant' },
			{ slugs: ['promotions_content'],                                    route: '/marketing/promotions' },
			{ slugs: ['payqart'],                                               route: '/payqart/payqart-overview' },
			{ slugs: ['fleet'],                                                 route: '/fleet-management/rider-management' },
			{ slugs: ['dispute_support'],                                       route: '/dashboard' }, // TODO: link to dispute support page when ready
			{ slugs: ['reports_exports'],                                       route: '/dashboard' }, // TODO: link to reports & exports page when ready
			{ slugs: ['system_configuration'],                                  route: '/users/staffs' },
		];

		for (const entry of priority) {
			if (this.hasAnyModuleAccess(entry.slugs)) return entry.route;
		}

		return '/login';
	}

}
